import * as React from "react"
import { Modal, Form, Button, Col, Spinner } from "react-bootstrap"
import { Subscription, Subject } from "rxjs"
import { map, debounceTime, distinctUntilChanged } from "rxjs/operators"
import { toast } from "react-toastify"
import LabAccount from "../../../api/LabService/LabServiceModel/LabAccount"
import GovPoolCreateObservable from "../../../api/Randoms/GovPoolCreate"
import GovPoolCreate from "../../../api/Randoms/GovPoolModel/GovPoolCreate"
import { Day } from "../../../api/Randoms/GovPoolModel/DayOfWeek"
import { isCompanyAdmin, isPortalAdmin } from "../../../authentication/isAdminRole"
import { AppState } from "../../../AppState"
import { connect } from "react-redux"
import NumericInput from "react-numeric-input"
import { SampleType, GovPanel } from "../../../api/Randoms/GovPoolModel/CollectionTypeResponse"
import { WeekdaysToExclude } from "./WeekdaysToExclude"
import GovDevice from "../../../api/Randoms/GovPoolModel/GovDevice"
import GovCollection from "../../OrderRequests/GovCollection"
import { MaxDateMonthly, MaxDateWeekly, MinDate } from "../../../config/Constants"
import { DaysToExcludeExt } from "./RandomSchedulingExclusionsModal"
import GetDaysToExclude from "../../../api/Randoms/GovDaysToExclude"
import DatesToExclude from "./DatesToExclude"
import DatesToExcludeModal from "./DatesToExcludeModal"

interface AddPoolModalProps {
  onHide: () => void
  onSuccess: () => void
}

interface AddPoolModalAuthProps {
  selectedCompany?: string
  isCompanyAdmin?: boolean
  isPortalAdmin?: boolean
}

interface AddPoolModalState {
  groupName: string
  selectionInterval: string
  selectionFrom: number
  selectionTo: number
  labAccount: string
  sampleType: string
  panel: string
  device: string
  daysToExclude: DaysToExcludeExt[]
  groupNameValid: boolean
  saving: boolean
  loadingAllGroupsExclusions: boolean
  isPOCTDevice?: boolean
  isAccountServiceAvailable?: boolean
  minSelectionFrom: number
  maxSelectionFrom: number
  minSelectionTo: number
  maxSelectionTo: number
}

export class AddGovPoolModal extends React.PureComponent<AddPoolModalProps & AddPoolModalAuthProps, AddPoolModalState> {
  state: AddPoolModalState = {
    groupName: "",
    selectionInterval: "",
    selectionFrom: 1,
    selectionTo: 1,
    labAccount: "",
    sampleType: "",
    panel: "",
    device: "",
    daysToExclude: [],
    groupNameValid: true,
    saving: false,
    loadingAllGroupsExclusions: false,
    isPOCTDevice: false,
    isAccountServiceAvailable: true,
    minSelectionFrom: MinDate,
    maxSelectionFrom: MaxDateMonthly,
    minSelectionTo: MinDate,
    maxSelectionTo: MaxDateMonthly
  }

  groupName$ = new Subject<React.ChangeEvent<HTMLInputElement>>();
  groupNameSubscription?: Subscription
  allGroupsExclusionsLoadSubscription?: Subscription
  saveSubscription?: Subscription

  componentDidMount() {
    this.setState({ loadingAllGroupsExclusions: true })
    this.allGroupsExclusionsLoadSubscription = GetDaysToExclude().subscribe(
      result => this.setState({ daysToExclude: result, loadingAllGroupsExclusions: false }),
      error => {
        this.setState({ loadingAllGroupsExclusions: false })
        toast.error(error?.errorData?.message || error.message)
      }
    )

    this.groupNameSubscription = this.groupName$.pipe(
      map(e => e.target.value),
      debounceTime(500),
      distinctUntilChanged()
    ).subscribe(value => {
      this.setState({ groupName: value, groupNameValid: value.length >= 8 })
    });

    const link = sessionStorage.getItem("company-selection-link") || ""
  }

  componentWillUnmount() {
    this.saveSubscription && this.saveSubscription.unsubscribe()
    this.allGroupsExclusionsLoadSubscription && this.allGroupsExclusionsLoadSubscription.unsubscribe()
    this.groupNameSubscription && this.groupName$.unsubscribe()
  }

  handleGroupNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    this.groupName$.next(event)
  }

  handleIntervalChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const interval = event.target.value
    const { minSelectionFrom, daysToExclude: days = [] } = this.state
    const maxSelectionFrom = this.maxSelectionTo(days, interval)

    this.setState({
      selectionInterval: interval,
      maxSelectionFrom: maxSelectionFrom,
      selectionFrom: minSelectionFrom,
      selectionTo: minSelectionFrom,
      minSelectionTo: minSelectionFrom,
      maxSelectionTo: maxSelectionFrom
    })
  }

  handleNumberOfSelectionChange = (value: React.ChangeEvent<HTMLInputElement>) => {
    const minSelectionFrom = Number(value)
    this.setState({ selectionFrom: minSelectionFrom, minSelectionTo: minSelectionFrom })
  }

  handleMaxNumberSelections = (value: React.ChangeEvent<HTMLInputElement>) => this.setState({ selectionTo: Number(value) })
  handleSampleTypeChange = (sampleType?: SampleType, isPOCTDevice?: boolean) => this.setState({ sampleType: sampleType?.SampleTypeID.toString() || "", isPOCTDevice: isPOCTDevice })
  handleLabChange = (lab?: LabAccount) => this.setState({ labAccount: lab?.LabAccount || "" })
  handlePanelChange = (panel?: GovPanel) => this.setState({ panel: panel?.ID || "" })
  handleDeviceChange = (device?: GovDevice) => this.setState({ device: device?.Code || "", panel: device?.PanelCode || "" })

  handleWeekdayToExcludeChange = (event: React.ChangeEvent<HTMLInputElement>, day: Day) => {
    const checked = event.target.checked
    const list = [...this.state.daysToExclude]
    const index = list.findIndex(item => item.DayOfWeekID === day)
    if (index === -1) {
      if (checked) {
        list.push({ ImplementationID: 0, AccountID: 0, DaysToExcludeID: 0, DayOfWeekID: day })
      }
    } else {
      list[index] = { ...list[index], removed: !checked }
    }

    this.putDaysToExcludeToState(list)
  }

  handleDaysToExcludeChange = (list: DaysToExcludeExt[]) => this.putDaysToExcludeToState(list)

  putDaysToExcludeToState = (list: DaysToExcludeExt[]) => {
    const { selectionInterval, selectionTo, selectionFrom } = this.state

    const activeDaysToExclude = list.filter(item => !item.removed)
    const maxSelectionFrom = this.maxSelectionTo(activeDaysToExclude, selectionInterval)
    this.setState({
      daysToExclude: list,
      maxSelectionFrom: maxSelectionFrom,
      selectionFrom: this.calcIntervalValue(activeDaysToExclude, selectionInterval, selectionFrom),
      maxSelectionTo: maxSelectionFrom,
      selectionTo: this.calcIntervalValue(activeDaysToExclude, selectionInterval, selectionTo)
    })
  }

  maxSelectionTo = (days: DaysToExcludeExt[], selectionInterval: string) => {
    switch (selectionInterval) {
      case "Weekly":
        return MaxDateWeekly - days.filter(item => item.DayOfWeekID).length
      case "Monthly":
        return MaxDateMonthly - days.filter(item => item.DayOfWeekID).length * 4
      default:
        return MaxDateMonthly
    }
  }

  calcIntervalValue = (days: DaysToExcludeExt[], selectionInterval: string, currentValue: number) => {
    const maxSelectionFrom = this.maxSelectionTo(days, selectionInterval)
    return currentValue === 0 || currentValue > maxSelectionFrom ? maxSelectionFrom : currentValue
  }

  isValid = () => {
    return this.state.groupName
      && this.state.groupNameValid
      && this.state.selectionInterval
      && this.state.selectionFrom
      && this.state.labAccount
      && this.state.panel
      && (!this.state.isPOCTDevice || (this.state.isPOCTDevice && this.state.device))
      && this.state.selectionFrom <= this.state.selectionTo
  }

  performSave = () => {
    const pool: GovPoolCreate = {
      Name: this.state.groupName,
      SelectionInterval: this.state.selectionInterval,
      NumberOfSelections: this.state.selectionFrom,
      MaxNumberSelections: this.state.selectionTo,
      LaboratoryAccount: this.state.labAccount,
      SampleTypeID: Number(this.state.sampleType),
      DefaultPanel: this.state.panel,
      DefaultDevice: this.state.isPOCTDevice ? this.state.device : "",
      Inactive: false,
      DaysExcluded: this.state.daysToExclude.map(item => {
        const { removed, ...rest } = item
        return { ...rest, DoRemove: (item.DaysToExcludeID ?? 0) > 0 && removed }
      })
    }
    this.setState({ saving: true })

    this.saveSubscription = GovPoolCreateObservable(pool).subscribe(
      () => {
        toast.success("Group created successfully.")
        this.setState({ saving: false })
        this.props.onSuccess()
      },
      error => {
        this.setState({ saving: false })
        toast.error(error?.errorData?.message || error.message)
      }
    )
  }
  
  daysToExcludeWithoutRemoved = () => this.state.daysToExclude.filter(item => !item.removed)

  render() {
    const { onHide } = this.props

    return <>
      <Modal backdrop="static" centered show onHide={onHide} size="xl">
        <Modal.Header closeButton>
          <Modal.Title>Create New Group</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Row className="form-group required">
              <Form.Label column sm={5} htmlFor="groupName">Group Name</Form.Label>
              <Col sm={7}>
                <Form.Control id="groupName" type="text" onChange={this.handleGroupNameChange} maxLength={65} isInvalid={!this.state.groupNameValid} />
                <Form.Control.Feedback type="invalid">Group Name should contain at least 8 characters</Form.Control.Feedback>
              </Col>
            </Form.Row>
            <Form.Row className="form-group required">
              <Form.Label column sm={5} htmlFor="selectionInterval">Selection Interval</Form.Label>
              <Col sm={7}>
                <Form.Control id="selectionInterval" as="select" onChange={this.handleIntervalChange}>
                  <option value="">Select</option>
                  <option value="Weekly">Weekly</option>
                  <option value="Monthly">Monthly</option>
                </Form.Control>
              </Col>
            </Form.Row>
            <Form.Row className="form-group">
              <Form.Label column sm={5} htmlFor="SelectionFrom" className="required">Number of Selections / Interval</Form.Label>
              <Col sm={2} />
              <Col sm={2}>
                <Form.Control as={NumericInput} id="SelectionFrom" min={this.state.minSelectionFrom} max={this.state.maxSelectionFrom} value={this.state.selectionFrom} strict onChange={this.handleNumberOfSelectionChange} />
              </Col>
              <Form.Label column sm={1} htmlFor="SelectionTo" className="text-right">To</Form.Label>
              <Col sm={2}>
                <Form.Control as={NumericInput} id="SelectionTo" strict
                  min={this.state.minSelectionTo}
                  max={this.state.maxSelectionTo}
                  value={this.state.selectionTo}
                  onChange={this.handleMaxNumberSelections} />
              </Col>
            </Form.Row>
            <GovCollection
              editing
              labAccount={this.state.labAccount || ""}
              serviceDOT={false}
              sampleTypeID={Number(this.state.sampleType)}
              panel={this.state.panel || ""}
              companyID={Number(this.props.selectedCompany)}
              onTypeChange={this.handleSampleTypeChange}
              onLabChange={this.handleLabChange}
              onPanelChange={this.handlePanelChange}
              onDeviceChange={this.handleDeviceChange} />
            <Form.Row className="form-group">
              <Form.Label column sm={5}>Days of Week to Exclude</Form.Label>
              <Col className="d-flex">
                {this.state.loadingAllGroupsExclusions
                  ? <Spinner animation="border" size="sm" />
                  : <WeekdaysToExclude editable daysToExclude={this.daysToExcludeWithoutRemoved()} handlerChange={this.handleWeekdayToExcludeChange} />}
              </Col>
            </Form.Row>
            <Form.Row>
              <Form.Label column sm={3}>Dates to Exclude</Form.Label>
              <Col sm={2} className="align-items-top">
                {!this.state.loadingAllGroupsExclusions &&
                  <DatesToExcludeModal
                    classNameButton="float-right"
                    poolID={0}
                    daysExcluded={this.state.daysToExclude}
                    onSave={this.handleDaysToExcludeChange} />}
              </Col>
              <Col sm={7} className="align-items-top">
                <DatesToExclude daysToExclude={this.daysToExcludeWithoutRemoved()} />
              </Col>
            </Form.Row>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide}>CANCEL</Button>
          <Button variant="primary" onClick={this.performSave} disabled={!this.isValid()}>SUBMIT</Button>
        </Modal.Footer>
      </Modal>
    </>
  }
}

function mapStateToProps(state: AppState): AddPoolModalAuthProps {
  return {
    selectedCompany: state.Workforce1State.selectedCompany,
    isCompanyAdmin: isCompanyAdmin(state),
    isPortalAdmin: isPortalAdmin(state)
  }
}
const ConnectedAddGovPoolModal = connect(mapStateToProps, null)(AddGovPoolModal)
export default ConnectedAddGovPoolModal
